// SEQ-180 phase 2, P24 / D27: the Total column rule for a manually entered
// operational metric on the project P&L tab.
//
// Run: deno test --allow-read src/features/pnlAnalytics/manualMetricTotals.test.ts
//
// No React and no supabase, so it Deno-tests in isolation exactly like
// operationalHoursTotals.ts next to it. Its one import is RATIO_UNITS, so
// the D10 unit list has a single definition shared with the registry and
// the widget validator rather than a third copy here.
//
// THE RULE (D27): a metric whose registry aggregation_method is 'avg' shows
// the unweighted mean of the months in range that CARRY A VALUE. A 'sum'
// metric keeps summing. Either way, no month with a value means null, never
// 0, so an empty band reads as "nothing entered" rather than "zero percent"
// (the same null-versus-zero rule operationalHoursTotals.ts already holds
// for payroll hours).
//
// WHY THE MEAN IS UNWEIGHTED: a manually typed percentage is one
// observation per project-month, and each observation counts once. A
// weighted mean would need a weight metric the registry does not carry.
// This matches the RPC's own `avg` semantics (plan section 6.2), so the
// tab's Total column and a company-grain widget answer the same question.
//
// WHY A MONTH WITH NO ROW IS EXCLUDED RATHER THAN COUNTED AS ZERO: it is
// the difference between "on-site rework ran at 4% across the three months
// anyone measured it" and "it ran at 1% across the year". Only the first is
// a claim the data supports. This is also why the band deletes a row when
// its cell is cleared instead of storing 0.

import { RATIO_UNITS } from "../../lib/pnl/pnlAnalytics";

/**
 * The two folds this band implements, a deliberate subset of
 * `PnlAggregationMethod` (widened to all six methods by P7). The editor
 * offers Hours, Count and Percent, which are only ever created as 'sum' or
 * 'avg'; 'min', 'max' and 'latest' are widget-level questions about a
 * bucket, not a project's own Total column, and 'calculated' never belongs
 * to a manually entered metric at all.
 */
export type ManualMetricAggregation = "sum" | "avg";

/**
 * Decides how one registry row's values fold, from the row's own unit and
 * aggregation_method.
 *
 * The unit wins over the stored method, deliberately. D10 makes
 * `unit IN ('percent','ratio','currency_per_hour') AND aggregation_method = 'sum'`
 * impossible going forward, but rows written before it existed are not
 * rewritten by that migration, and summing twelve monthly percentages is
 * the exact failure the CHECK was added to stop. Anything else falls back
 * to `sum`, which is right for hours and counts and is what an unset
 * column means.
 */
export function resolveManualAggregation(
  unit: string,
  aggregationMethod: string | null | undefined,
): ManualMetricAggregation {
  if ((RATIO_UNITS as readonly string[]).includes(unit)) return "avg";
  return aggregationMethod === "avg" ? "avg" : "sum";
}

/**
 * The Total column value for one manual metric row, over the months
 * currently in range. `null` entries are months with no value, which are
 * excluded from both folds rather than counted as 0.
 *
 * Returns `null` when no month carries a value; the caller renders the
 * "No values" placeholder for that, never a zero.
 */
export function totalManualMetric(
  monthValues: ReadonlyArray<number | null>,
  method: ManualMetricAggregation,
): number | null {
  const present: number[] = [];
  for (const value of monthValues) {
    if (value !== null) present.push(value);
  }
  if (present.length === 0) return null;

  const sum = present.reduce((a, b) => a + b, 0);
  return method === "avg" ? sum / present.length : sum;
}

/** How many months in range carry a value, for the "mean of 3 months" tooltip that keeps an unweighted average from surprising someone (plan section 12, risk 5). */
export function countMonthsWithValue(monthValues: ReadonlyArray<number | null>): number {
  let count = 0;
  for (const value of monthValues) {
    if (value !== null) count += 1;
  }
  return count;
}
